import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Link2, Check, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FixBlockerLink } from "@/components/fix-blocker-link";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

/**
 * AssignClientMeta — inline "assign a Facebook Page + linked Instagram to this client".
 *
 * Lets the user pick one of the agency's Meta pages (with its linked IG) right where
 * they hit the empty state (Asystent wiadomości, Analityka) instead of jumping to the
 * agency panel. Persists to agencyClients.facebookPageId/instagramActorId via the
 * existing PUT /api/agency/clients/:id — which the scheduler, automations and analytics
 * all read from. No backend change needed.
 */

interface MetaPage {
  id: string;
  name: string;
  instagramBusinessAccount?: { id: string; name?: string; username?: string };
}

interface Props {
  clientId: string;
  clientName?: string;
  /** Query keys to invalidate after assignment so the host panel re-pulls accounts. */
  invalidateKeys?: readonly (readonly unknown[])[];
  onAssigned?: () => void;
}

export function AssignClientMeta({
  clientId,
  clientName,
  invalidateKeys,
  onAssigned,
}: Props): JSX.Element {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [pageId, setPageId] = useState("");
  const [query, setQuery] = useState("");

  const { data, isLoading } = useQuery<{ pages: MetaPage[] }>({
    queryKey: ["/api/scheduler/pages", "agency-owner"],
    queryFn: () => apiRequest("GET", "/api/scheduler/pages"),
    staleTime: 60_000,
  });
  const pages = data?.pages ?? [];
  const selected = pages.find((p) => p.id === pageId) ?? null;

  const assign = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error("Wybierz Stronę Facebook");
      const ig = selected.instagramBusinessAccount;
      return apiRequest("PUT", `/api/agency/clients/${clientId}`, {
        facebookPageId: selected.id,
        facebookPageName: selected.name,
        instagramActorId: ig?.id ?? null,
        instagramActorName: ig?.username ?? ig?.name ?? null,
      });
    },
    onSuccess: () => {
      toast({
        title: "Przypisano konta",
        description: `${selected?.name}${selected?.instagramBusinessAccount ? ` + IG @${selected.instagramBusinessAccount.username ?? selected.instagramBusinessAccount.name ?? ""}` : ""}`,
      });
      void qc.invalidateQueries({ queryKey: ["/api/agency/clients"] });
      (invalidateKeys ?? []).forEach(
        (key) => void qc.invalidateQueries({ queryKey: key as unknown[] })
      );
      onAssigned?.();
    },
    onError: (err) =>
      toast({
        title: "Nie udało się przypisać",
        description:
          err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "Spróbuj ponownie",
        variant: "destructive",
      }),
  });

  return (
    <div className="w-full max-w-md space-y-2 bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-white">
        <Link2 size={14} className="text-[#ef3e29]" /> Przypisz konta do{" "}
        {clientName ? `„${clientName}"` : "klienta"}
      </div>
      <p className="text-[11px] text-white/45 leading-relaxed">
        Wybierz Stronę Facebook — powiązane konto Instagram Professional zostanie przypisane
        automatycznie.
      </p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-white/40 text-xs py-2">
          <Loader2 size={13} className="animate-spin" /> Ładowanie kont…
        </div>
      ) : pages.length === 0 ? (
        <div className="py-1 space-y-1.5">
          <p className="text-[11px] text-amber-200/80">
            Brak dostępnych Stron — podłącz Metę agencji w Centrum Integracji.
          </p>
          <FixBlockerLink label="Podłącz Metę agencji" open="meta" className="text-[11px]" />
        </div>
      ) : (
        <>
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj strony lub @ig…"
              className="w-full bg-[#0a0c10] border border-white/[0.12] rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#ef3e29]/50"
            />
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-white/[0.08] divide-y divide-white/[0.04]">
            {pages
              .filter((p) => {
                const q = query.trim().toLowerCase();
                if (!q) return true;
                const ig = p.instagramBusinessAccount;
                return (
                  p.name.toLowerCase().includes(q) ||
                  (ig?.username ?? "").toLowerCase().includes(q) ||
                  (ig?.name ?? "").toLowerCase().includes(q)
                );
              })
              .map((p) => {
                const ig = p.instagramBusinessAccount;
                const isSel = pageId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPageId(p.id)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors ${isSel ? "bg-[#ef3e29]/15" : "hover:bg-white/[0.04]"}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-xs text-white truncate">{p.name}</span>
                      <span className="block text-[10px] text-white/40 truncate">
                        {ig ? `IG @${ig.username ?? ig.name ?? ""}` : "bez IG"}
                      </span>
                    </span>
                    {isSel && <Check size={14} className="text-[#ef3e29] shrink-0" />}
                  </button>
                );
              })}
            {pages.filter((p) => {
              const q = query.trim().toLowerCase();
              if (!q) return true;
              const ig = p.instagramBusinessAccount;
              return (
                p.name.toLowerCase().includes(q) ||
                (ig?.username ?? "").toLowerCase().includes(q) ||
                (ig?.name ?? "").toLowerCase().includes(q)
              );
            }).length === 0 && (
              <p className="px-3 py-3 text-[11px] text-white/35 text-center">
                Brak wyników dla „{query}"
              </p>
            )}
          </div>
          <Button
            size="sm"
            disabled={!pageId || assign.isPending}
            onClick={() => assign.mutate()}
            className="w-full bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white"
          >
            {assign.isPending ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <Check size={13} className="mr-1.5" />
            )}
            Przypisz do klienta
          </Button>
        </>
      )}
    </div>
  );
}
